import type { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { frontOverhangAllowed } from "@CommonUtils/pog/frontOverhang";
import { usableShelfDepth } from "@CommonUtils/pog/shelfLoader";
import { translate } from "src/i18n/translate";
import { DEFAULT_LENGTH_UNIT, formatLength } from "src/utils/lengthUnit";

export interface FrontOverhangMarker {
  label: string;
  description: string;
  invalid: boolean;
}

export const OVERHANG_COLOR = "#0369a1";
export const OVERHANG_INVALID_COLOR = "#b45309";

export const shelfFrontOverhangMarker = (
  shelf: PlanogramShelf,
  unit = DEFAULT_LENGTH_UNIT,
): FrontOverhangMarker | null => {
  const allowance = shelf.maxFrontOverhang ?? 0;
  if (!(allowance > 0)) return null;
  const invalid =
    shelf.type !== "REGULAR" ||
    !!shelf.slope ||
    !!shelf.mergedLeft ||
    !!shelf.mergedRight ||
    !(usableShelfDepth(shelf) > 0) ||
    !Number.isFinite(allowance);
  return {
    label: translate(
      "common",
      invalid ? "frontOverhang.marker.shelfCheck" : "frontOverhang.marker.shelfAllowed",
    ),
    description: translate(
      "common",
      invalid
        ? "frontOverhang.marker.shelfDescriptionIneligible"
        : "frontOverhang.marker.shelfDescription",
      { allowance: formatLength(allowance, unit) },
    ),
    invalid,
  };
};

export const itemFrontOverhangMarker = (
  item: PlanogramItem,
  unit = DEFAULT_LENGTH_UNIT,
): FrontOverhangMarker | null => {
  if (
    !item.shelf.bay.planogram.pog.frontOverhangEnabled &&
    !((item.shelf.maxFrontOverhang ?? 0) > 0)
  )
    return null;
  const depth = item.getDepth();
  const support = usableShelfDepth(item.shelf);
  if (!Number.isFinite(depth) || !Number.isFinite(support) || depth <= 0 || depth <= support)
    return null;
  const excess = depth - support;
  const percent = Math.ceil((excess / depth) * 1000) / 10;
  const invalid = !frontOverhangAllowed(item) || item.getDepthFacings() !== 1;
  return {
    label: `${invalid ? "!" : "↗"} ${percent}%`,
    description: translate(
      "common",
      invalid
        ? "frontOverhang.marker.itemDescriptionInvalid"
        : "frontOverhang.marker.itemDescriptionValid",
      {
        excess: formatLength(excess, unit),
        percent,
        depth: formatLength(depth, unit),
        support: formatLength(support, unit),
      },
    ),
    invalid,
  };
};
